import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { budVision, postsApi } from "@/lib/api";
import { useStateCtx } from "@/context/StateContext";
import { useAuth } from "@/context/AuthContext";
import { Camera, ImagePlus, Loader2, Eye, Sparkles, X, Image as ImageIcon, Send, Flame } from "lucide-react";
import { toast } from "sonner";
import LiveCameraCapture from "@/components/LiveCameraCapture";
import BudSpeak from "@/components/BudSpeak";
import BudPersonalityPicker from "@/components/BudPersonalityPicker";

export default function BudVision() {
  const { stateCode } = useStateCtx();
  const { user } = useAuth();
  const nav = useNavigate();
  const [imgB64, setImgB64] = useState("");
  const [imgPreview, setImgPreview] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [matched, setMatched] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [persona, setPersona] = useState(() => {
    try { return localStorage.getItem("xseed.bud_persona") || "stoner"; } catch { return "stoner"; }
  });
  const [voice, setVoice] = useState(() => {
    try { return localStorage.getItem("xseed.bud_voice") || "onyx"; } catch { return "onyx"; }
  });
  const inputRef = useRef(null);

  const onPersona = (p) => {
    setPersona(p.key);
    setVoice(p.voice);
    try {
      localStorage.setItem("xseed.bud_persona", p.key);
      localStorage.setItem("xseed.bud_voice", p.voice);
    } catch (e) { /* ignore */ }
  };

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 5_000_000) { toast.error("Image too big — under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      setImgB64(b64);
      setImgPreview(reader.result);
      setReply("");
      setMatched(null);
    };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!user) { nav("/auth?mode=login"); return; }
    if (!imgB64) { toast.error("Drop a flower pic first"); return; }
    setBusy(true);
    setReply("");
    setMatched(null);
    try {
      const r = await budVision.analyze(imgB64, stateCode === "ALL" ? "CO" : stateCode, note || null, persona);
      setReply(r.reply || "");
      setMatched(r.matched);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bud's vision is foggy. Try again.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setImgB64(""); setImgPreview(""); setReply(""); setMatched(null); setNote("");
  };

  return (
    <div className="space-y-5">
      <div className="card-x card-purple bg-[#FAF6E8] p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Eye size={18} strokeWidth={2.4} />
          <span className="font-display text-2xl font-extrabold">Show Bud the nug.</span>
        </div>
        <p className="text-sm opacity-70 mb-4">
          Drop a flower photo. Bud the weedologist will break down the look, give you a GAS verdict, guess the strain, describe the feel — and pick the closest match from your state's catalog.
        </p>

        {/* Personality picker */}
        <div className="mb-4">
          <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-65 mb-2">Bud's mood</div>
          <BudPersonalityPicker value={persona} onChange={onPersona} />
        </div>

        {!imgPreview ? (
          <div
            data-testid="bud-vision-drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            className="aspect-[16/10] border-2 border-dashed border-[#0B0F0C] rounded-2xl bg-[#FAF6E8] flex items-center justify-center text-center p-6"
          >
            <div>
              <ImagePlus size={32} className="mx-auto mb-2" />
              <div className="font-mono-x font-bold uppercase tracking-widest text-sm">Drop a photo · or pick a button</div>
              <div className="text-xs opacity-60 mt-1">JPG · PNG · WEBP · ≤ 5MB</div>
              <div className="grid grid-cols-2 gap-2 mt-4 max-w-xs mx-auto">
                <button
                  type="button"
                  data-testid="bud-vision-take-photo"
                  onClick={() => setLiveOpen(true)}
                  className="btn-x btn-primary !py-2 !px-3 !text-xs justify-center"
                >
                  <Camera size={12} strokeWidth={2.6} /> Take photo
                </button>
                <button
                  type="button"
                  data-testid="bud-vision-choose-photo"
                  onClick={() => inputRef.current?.click()}
                  className="btn-x btn-ghost !py-2 !px-3 !text-xs justify-center"
                >
                  <ImageIcon size={12} strokeWidth={2.4} /> Choose photo
                </button>
              </div>
            </div>
            <input
              ref={inputRef}
              data-testid="bud-vision-input"
              type="file" accept="image/*"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="hidden"
            />
          </div>
        ) : (
          <div className="relative">
            <img src={imgPreview} alt="" className="w-full max-h-[420px] object-contain bg-[#0B0F0C] rounded-2xl border-2 border-[#0B0F0C]" />
            <button
              onClick={reset}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#FAF6E8] border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white"
            ><X size={13} strokeWidth={2.4} /></button>
          </div>
        )}

        {liveOpen && (
          <LiveCameraCapture
            onClose={() => setLiveOpen(false)}
            onCapture={(b64, dataUrl) => {
              setImgB64(b64);
              setImgPreview(dataUrl);
              setReply("");
              setMatched(null);
            }}
          />
        )}

        <input
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Anything specific? (optional · e.g., 'is this top-shelf?')"
          className="mt-4 w-full bg-white border-2 border-[#0B0F0C] rounded-full px-4 h-11 text-sm outline-none focus:shadow-[3px_3px_0_#0B0F0C] transition-all"
        />

        <button
          data-testid="bud-vision-analyze"
          onClick={analyze} disabled={busy || !imgB64}
          className="btn-x btn-primary w-full justify-center mt-4 disabled:opacity-50"
        >
          {busy ? <><Loader2 size={14} className="animate-spin" /> Bud's looking…</> : <>Ask Bud <Sparkles size={14} strokeWidth={2.4} /></>}
        </button>
      </div>

      {reply && (
        <div data-testid="bud-vision-reply" className="card-x card-green bg-[#FAF6E8] p-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-3xl">🌿</span>
            <span className="font-display text-2xl font-extrabold">Bud's read</span>
            <div className="ml-auto"><BudSpeak text={reply} defaultVoice={voice} compact showPicker={false} /></div>
          </div>
          <div className="text-sm leading-relaxed whitespace-pre-line font-medium mb-4">{reply}</div>
          <div className="flex flex-wrap items-center gap-2">
            <BudSpeak text={reply} defaultVoice={voice} />
            {/[Gg][Aa][Ss]/.test(reply) && imgB64 && (
              <button
                data-testid="share-gas-to-feed"
                onClick={async () => {
                  if (!user) { nav("/auth?mode=login"); return; }
                  try {
                    const captionLine = "Bud said it's GAS 🔥 — drop your verdict ↓";
                    await postsApi.create({
                      image_base64: imgB64,
                      caption: `${captionLine}\n\n${reply.slice(0, 400)}`.trim(),
                      verdict: "gas",
                      product_id: matched?.id || null,
                      flavor_tags: [],
                    });
                    toast.success("Shared to community feed (+5 XP)");
                    nav("/community");
                  } catch (e) {
                    toast.error("Couldn't share");
                  }
                }}
                className="btn-x btn-primary !py-2 !px-4 !text-xs"
              >
                <Flame size={12} strokeWidth={2.6} /> Share to feed
              </button>
            )}
          </div>
        </div>
      )}

      {matched && (
        <Link to={`/product/${matched.id}`} className="card-x card-magenta bg-[#FAF6E8] p-5 flex items-center gap-4 hover:scale-[1.01] transition-transform">
          {matched.image_url && <img src={matched.image_url} alt="" className="w-20 h-20 rounded-2xl object-cover border-2 border-[#0B0F0C]" />}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FF1A8C] mb-1">Bud's pick from your state</div>
            <div className="font-display text-2xl font-extrabold leading-tight truncate">{matched.name}</div>
            <div className="text-xs font-mono-x opacity-70">{matched.strain} · {matched.thc?.toFixed(0)}% THC · ${matched.price}</div>
          </div>
          <span className="sticker sticker-green">VALUE {matched.value_score}/10</span>
        </Link>
      )}
    </div>
  );
}
